import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import AdminFormActions from '../../components/admin/shared/AdminFormActions'
import AdminFormCard from '../../components/admin/shared/AdminFormCard'
import type { AdminFormTab } from '../../components/admin/shared/AdminFormTabHeader'
import FormErrorSummary from '../../components/admin/shared/FormErrorSummary'
import NoticeBar from '../../components/shared/NoticeBar'
import {
  AdminFormSection,
  ChipMultiSelect,
  LabeledSelect,
  LabeledTextField,
  ToggleField,
} from '../../components/admin/shared/fields'
import {
  Box,
  Container,
  Button,
  MenuItem,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material'
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { poolwerxColors } from '../../theme/colors'
import { apiClient } from '../../services/api'
import { userApi, CreateUserRequest, UpdateUserRequest, UserFranchiseAccess } from '../../services/userApi'
import { FormTabPanel } from '../../components/shared/FormTabPanel'
import { useNotify } from '../../components/shared/ToastProvider'
import { useAuth } from '../../contexts/AuthContext'
import { useFormTabs } from '../../hooks/useFormTabs'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { firstErrorTab, hasErrors } from '../../utils/formErrors'
import { ADMIN_TABLE_HEADER_SX, inlineActionIconSx } from '../../components/admin/adminTableStyles'
import {
  isInternalEmailDomain,
  isAllowedEmailDomain,
  getInternalDomainsDisplay,
  getAllDomainsDisplay,
} from '../../utils/emailValidation'

type UserRole = 'EVO_ADMIN' | 'EVO_TEAM' | 'PSC_TEAM' | 'FP_USER'

const INTERNAL_ROLES: UserRole[] = ['EVO_ADMIN', 'EVO_TEAM', 'PSC_TEAM']
const ALL_ROLES: UserRole[] = ['EVO_ADMIN', 'EVO_TEAM', 'PSC_TEAM', 'FP_USER']
const MARKET_OPTIONS = [
  { label: 'ANZ', value: 'ANZ' },
  { label: 'USA', value: 'USA' },
]

const ROLE_LABELS: Record<UserRole, string> = {
  EVO_ADMIN: 'Evo Admin',
  EVO_TEAM: 'Evo Team',
  PSC_TEAM: 'PSC Team',
  FP_USER: 'FP User'
}

interface FPOption {
  fp_code: string
  franchise_name: string
}

const AVAILABLE_FEATURES: Array<{ key: string; label: string; description: string }> = [
  { key: 'franchise_partners', label: 'Franchise Partners', description: 'FP management, system connections, and FP user management' },
  { key: 'data_exceptions', label: 'Data Exceptions', description: 'Exception overview and tracking' },
  { key: 'health_scores', label: 'Health Scores', description: 'Health score dashboards' },
  { key: 'platform_reports', label: 'Platform Reports', description: 'Cross-franchise reports and exports' },
  { key: 'data_sync', label: 'Data Sync', description: 'Sync history, triggers, monitoring' },
  { key: 'user_management', label: 'User Management', description: 'Full admin Users area (all users, invitations, and access)' },
  { key: 'system_admin', label: 'System Admin', description: 'System status, audit logs, settings, admin tools' },
  { key: 'notifications', label: 'Notifications', description: 'System email notifications' },
]

const DEFAULT_EVO_TEAM_FEATURES = [
  'franchise_partners', 'data_exceptions', 'health_scores', 'platform_reports', 'data_sync',
]

interface FormData {
  email: string
  name: string
  role: UserRole
  is_active: boolean
  allowed_markets: string[]
  allowed_features: string[]
  system_emails: boolean
}

const initialFormData: FormData = {
  email: '',
  name: '',
  role: 'PSC_TEAM',
  is_active: true,
  allowed_markets: [],
  allowed_features: [],
  system_emails: false
}

// Human labels + owning tab for the uniform validation summary.
const USER_FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  name: 'Name',
  role: 'Role',
  allowed_markets: 'Markets',
  allowed_features: 'Feature Access',
  system_emails: 'System Emails',
}
const USER_FIELD_TO_SLUG: Record<string, string> = {
  email: 'general', name: 'general', role: 'general', is_active: 'general',
  allowed_markets: 'permissions', allowed_features: 'permissions',
  system_emails: 'notifications',
}
const USER_FIELD_ANCHORS: Record<string, string> = {
  email: 'uf-email',
  name: 'uf-name',
}

const isInternalRole = (role: UserRole): boolean => {
  return INTERNAL_ROLES.includes(role)
}

const UserForm: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const [searchParams] = useSearchParams()
  const isEditMode = !!id
  const { user: viewer } = useAuth()
  const { notify } = useNotify()
  const isEvoAdmin = viewer?.role === 'EVO_ADMIN'
  const availableRoles: UserRole[] = isEvoAdmin ? ALL_ROLES : ['FP_USER']

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState<FormData>(
    isEvoAdmin ? initialFormData : { ...initialFormData, role: 'FP_USER' }
  )
  // Snapshot of the last-saved state for the unsaved-changes guard.
  const [originalFormData, setOriginalFormData] = useState<FormData>(
    isEvoAdmin ? initialFormData : { ...initialFormData, role: 'FP_USER' }
  )
  const isReadOnlyForViewer = isEditMode && !isEvoAdmin && formData.role !== 'FP_USER'

  const [fpAccess, setFpAccess] = useState<UserFranchiseAccess[]>([])
  const [pendingFpCodes, setPendingFpCodes] = useState<FPOption[]>([])
  const [fpOptions, setFpOptions] = useState<FPOption[]>([])
  const [fpSearchText, setFpSearchText] = useState('')
  const [fpLoading, setFpLoading] = useState(false)
  const [selectedFp, setSelectedFp] = useState<FPOption | null>(null)

  // Tab order depends on role: FP users get General + FP Access; internal
  // users get General + Permissions + Notifications. `useFormTabs` keeps the
  // active tab addressable via `#slug` and in sync with the back button.
  const isFpUser = formData.role === 'FP_USER'
  const visibleSlugs = isFpUser
    ? ['general', 'fp-access']
    : ['general', 'permissions', 'notifications']
  const { tabValue, setTabValue, handleTabChange } = useFormTabs(visibleSlugs)
  const fieldToTab: Record<string, number> = Object.fromEntries(
    Object.entries(USER_FIELD_TO_SLUG)
      .map(([field, slug]) => [field, visibleSlugs.indexOf(slug)])
      .filter(([, idx]) => (idx as number) >= 0),
  )

  const { confirmDiscard } = useUnsavedChangesGuard(formData, originalFormData, {
    suppressed: saving,
  })

  useEffect(() => {
    if (isEditMode && id) {
      loadUser(parseInt(id, 10))
    }
  }, [isEditMode, id])

  useEffect(() => {
    const fpParam = searchParams.get('fp')
    if (fpParam && !isEditMode) {
      setFormData(prev => ({ ...prev, role: 'FP_USER' }))
      loadFpByCode(fpParam)
    }
  }, [searchParams, isEditMode])

  useEffect(() => {
    if (fpSearchText.length >= 2) {
      searchFps(fpSearchText)
    }
  }, [fpSearchText])

  const loadUser = async (userId: number) => {
    setLoading(true)
    setError(null)
    try {
      const response = await userApi.getUser(userId)
      const user = response.data
      const loaded: FormData = {
        email: user.email || '',
        name: user.name || '',
        role: user.role,
        is_active: user.is_active ?? true,
        allowed_markets: user.allowed_markets || [],
        allowed_features: user.allowed_features || [],
        system_emails: user.notification_preferences?.system_emails ?? false
      }
      setFormData(loaded)
      setOriginalFormData(loaded)
      setFpAccess(user.franchise_access || [])
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load user')
    } finally {
      setLoading(false)
    }
  }

  const loadFpByCode = async (fpCode: string) => {
    try {
      const response = await apiClient.get(`/franchise-partners/${fpCode}`)
      const fp = response.data
      const option: FPOption = {
        fp_code: fp.fp_code,
        franchise_name: fp.franchise_name
      }
      setPendingFpCodes(prev => {
        if (prev.some(p => p.fp_code === option.fp_code)) return prev
        return [...prev, option]
      })
    } catch (err) {
      console.error('Failed to load FP by code:', err)
      notify({ message: 'Could not load that franchise partner.', severity: 'warning' })
    }
  }

  const searchFps = async (query: string) => {
    setFpLoading(true)
    try {
      const response = await apiClient.get('/franchise-partners/search', {
        params: { q: query }
      })
      setFpOptions(response.data || [])
    } catch (err) {
      console.error('Failed to search FPs:', err)
      notify({ message: 'Franchise partner search failed. Please try again.', severity: 'warning' })
    } finally {
      setFpLoading(false)
    }
  }

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'role') {
        if (value === 'EVO_TEAM' && prev.allowed_features.length === 0) {
          next.allowed_features = [...DEFAULT_EVO_TEAM_FEATURES]
        } else if (value === 'EVO_ADMIN') {
          next.allowed_features = []
        }
      }
      return next
    })
    if (field === 'role') {
      setTabValue(0)
    }
  }

  const handleAddFpAccess = async () => {
    if (!selectedFp) return

    if (isEditMode && id) {
      try {
        await userApi.grantFPAccess(parseInt(id, 10), selectedFp.fp_code)
        await loadUser(parseInt(id, 10))
        setSelectedFp(null)
        setFpSearchText('')
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to grant FP access')
      }
    } else {
      if (!pendingFpCodes.some(p => p.fp_code === selectedFp.fp_code)) {
        setPendingFpCodes(prev => [...prev, selectedFp])
      }
      setSelectedFp(null)
      setFpSearchText('')
    }
  }

  const handleRemoveFpAccess = async (fpId: number, fpCode: string) => {
    if (isEditMode && id) {
      try {
        await userApi.revokeFPAccess(parseInt(id, 10), fpId)
        await loadUser(parseInt(id, 10))
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to revoke FP access')
      }
    } else {
      setPendingFpCodes(prev => prev.filter(p => p.fp_code !== fpCode))
    }
  }

  // Client-side validation -> field-keyed map consumed by FormErrorSummary.
  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {}
    if (!formData.email) {
      errs.email = 'Email is required'
    } else if (isInternalRole(formData.role)) {
      if (!isInternalEmailDomain(formData.email)) {
        errs.email = `Internal users must have a ${getInternalDomainsDisplay()} email address`
      }
    } else if (!isAllowedEmailDomain(formData.email)) {
      errs.email = `Email must be one of the allowed domains: ${getAllDomainsDisplay()}`
    }
    if (!formData.name) errs.name = 'Name is required'
    if (formData.role === 'PSC_TEAM' && formData.allowed_markets.length === 0) {
      errs.allowed_markets = 'Select at least one market for PSC Team members'
    }
    if (formData.role === 'EVO_TEAM' && formData.allowed_features.length === 0) {
      errs.allowed_features = 'Select at least one feature for team members'
    }
    return errs
  }

  const handleSubmit = async () => {
    setError(null)
    const errs = validate()
    setValidationErrors(errs)
    if (hasErrors(errs)) {
      const tab = firstErrorTab(errs, fieldToTab)
      if (tab !== null) setTabValue(tab)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    setSaving(true)
    try {
      const sendFeatures = formData.role === 'EVO_TEAM' || formData.role === 'PSC_TEAM'
      if (isEditMode && id) {
        const updateData: UpdateUserRequest = {
          role: formData.role,
          is_active: formData.is_active,
          allowed_markets: formData.role === 'PSC_TEAM' ? formData.allowed_markets : undefined,
          allowed_features: sendFeatures ? formData.allowed_features : undefined,
          notification_preferences: isInternalRole(formData.role)
            ? { system_emails: formData.system_emails }
            : undefined
        }
        await userApi.updateUser(parseInt(id, 10), updateData)
      } else {
        const createData: CreateUserRequest = {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          allowed_markets: formData.role === 'PSC_TEAM' ? formData.allowed_markets : undefined,
          allowed_features: sendFeatures ? formData.allowed_features : undefined,
          fp_codes: formData.role === 'FP_USER' ? pendingFpCodes.map(fp => fp.fp_code) : undefined
        }
        await userApi.createUser(createData)
      }

      // Mark the form clean so the unsaved-changes guard lets the post-save
      // navigation through.
      setOriginalFormData(formData)
      setSuccessMessage(isEditMode ? 'User updated successfully' : 'User created successfully')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => {
        navigate('/admin/users')
      }, 1500)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      if (Array.isArray(detail)) {
        const fieldErrors: Record<string, string> = {}
        detail.forEach((e: any) => {
          const field = e.loc?.slice(1).join('.') || 'unknown'
          fieldErrors[field] = e.msg
        })
        setValidationErrors(fieldErrors)
        const tab = firstErrorTab(fieldErrors, fieldToTab)
        if (tab !== null) setTabValue(tab)
      } else {
        setError(detail || err.message || 'Failed to save user')
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (!confirmDiscard()) return
    navigate('/admin/users')
  }

  // Flag tabs holding a validation error (red dot on the tab).
  const erroredTabs = new Set(
    Object.entries(validationErrors)
      .filter(([, msg]) => Boolean(msg))
      .map(([field]) => fieldToTab[field])
      .filter((tab) => tab !== undefined),
  )
  const tabHasError = (slug: string) => erroredTabs.has(visibleSlugs.indexOf(slug))
  const tabs: AdminFormTab[] = isFpUser
    ? [
        { label: 'General', icon: <PersonIcon />, hasError: tabHasError('general') },
        { label: 'FP Access', icon: <BusinessIcon /> },
      ]
    : [
        { label: 'General', icon: <PersonIcon />, hasError: tabHasError('general') },
        { label: 'Permissions', icon: <SecurityIcon />, hasError: tabHasError('permissions') },
        { label: 'Notifications', icon: <NotificationsIcon />, hasError: tabHasError('notifications') },
      ]

  const fpAccessRows = useMemo(() => {
    if (isEditMode) return fpAccess
    return pendingFpCodes.map((fp, idx) => ({
      fp_id: idx,
      fp_code: fp.fp_code,
      franchise_name: fp.franchise_name,
      granted_at: 'Pending',
      granted_by: 0
    }))
  }, [isEditMode, fpAccess, pendingFpCodes])

  const filteredFpOptions = useMemo(() => {
    const existingCodes = isEditMode
      ? fpAccess.map(a => a.fp_code)
      : pendingFpCodes.map(p => p.fp_code)
    return fpOptions.filter(fp => !existingCodes.includes(fp.fp_code))
  }, [fpOptions, fpAccess, pendingFpCodes, isEditMode])

  const pageTitle = isEditMode
    ? `Edit ${formData.email || 'User'}`
    : 'Add New User'

  return (
    <Container maxWidth="lg" sx={{ mb: 3 }}>
      <AdminPageHeader
        pageKey="editUser"
        dense
        titleOverride={isEditMode ? 'Edit User' : 'Add User'}
        helmetTitle={pageTitle}
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'User Management', to: '/admin/users' },
          ...(isEditMode
            ? [{ label: formData.email || 'User' }, { label: 'Edit' }]
            : [{ label: 'Add New' }]),
        ]}
      />

      <FormErrorSummary
        message={error}
        errors={validationErrors}
        fieldLabels={USER_FIELD_LABELS}
        fieldToTab={fieldToTab}
        fieldAnchors={USER_FIELD_ANCHORS}
        onJumpToTab={setTabValue}
      />

      {successMessage && (
        <NoticeBar severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </NoticeBar>
      )}

      {isReadOnlyForViewer && (
        <NoticeBar severity="info" sx={{ mb: 2 }}>
          Only an EVO_ADMIN can modify this user. You can view but not change their details.
        </NoticeBar>
      )}

      <AdminFormCard tabs={tabs} value={tabValue} onChange={handleTabChange}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tab 0: General */}
            <FormTabPanel idPrefix="user" value={tabValue} index={0}>
              <Grid container spacing={3}>
                <AdminFormSection title="User Details" card>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <LabeledTextField
                        id="uf-email"
                        label="Email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        disabled={isEditMode}
                        helperText={
                          isEditMode
                            ? 'Email cannot be changed after creation'
                            : isInternalRole(formData.role)
                              ? `Must be a ${getInternalDomainsDisplay()} email`
                              : `Must be one of: ${getAllDomainsDisplay()}`
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <LabeledTextField
                        id="uf-name"
                        label="Name"
                        required
                        value={formData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        disabled={isEditMode}
                        helperText={isEditMode ? 'Name cannot be changed after creation' : 'Full name'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <LabeledSelect
                        label="Role"
                        value={formData.role}
                        onChange={(e) => handleFieldChange('role', e.target.value as UserRole)}
                        disabled={!isEvoAdmin}
                      >
                        {availableRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {ROLE_LABELS[role]}
                          </MenuItem>
                        ))}
                      </LabeledSelect>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Box sx={{ width: '100%' }}>
                        <ToggleField
                          label="Active"
                          description="Inactive users cannot sign in."
                          checked={formData.is_active}
                          onChange={(checked) => handleFieldChange('is_active', checked)}
                          activeColor="success"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </AdminFormSection>
              </Grid>
            </FormTabPanel>

            {/* Tab 1 (internal): Permissions */}
            {isInternalRole(formData.role) && (
              <FormTabPanel idPrefix="user" value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <AdminFormSection
                    title="Market Permissions"
                    card
                    caption={
                      formData.role === 'PSC_TEAM'
                        ? 'Select which markets this PSC team member can access. At least one is required.'
                        : undefined
                    }
                  >
                    {formData.role === 'PSC_TEAM' ? (
                      <ChipMultiSelect
                        options={MARKET_OPTIONS}
                        value={formData.allowed_markets}
                        onChange={(markets) => handleFieldChange('allowed_markets', markets)}
                      />
                    ) : (
                      <Alert severity="info">
                        {ROLE_LABELS[formData.role]} users have access to all markets by default.
                        Market restrictions are only available for PSC Team members.
                      </Alert>
                    )}
                  </AdminFormSection>

                  {(formData.role === 'EVO_TEAM' || formData.role === 'PSC_TEAM') && (
                    <AdminFormSection
                      title="Admin Panel Feature Access"
                      card
                      caption="Grant access to specific admin features for this team member."
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {AVAILABLE_FEATURES.map((feature) => (
                          <ToggleField
                            key={feature.key}
                            label={feature.label}
                            description={feature.description}
                            checked={formData.allowed_features.includes(feature.key)}
                            onChange={() => {
                              const current = formData.allowed_features
                              const updated = current.includes(feature.key)
                                ? current.filter(f => f !== feature.key)
                                : [...current, feature.key]
                              handleFieldChange('allowed_features', updated)
                            }}
                          />
                        ))}
                      </Box>
                    </AdminFormSection>
                  )}
                </Grid>
              </FormTabPanel>
            )}

            {/* Tab 2 (internal): Notifications */}
            {isInternalRole(formData.role) && (
              <FormTabPanel idPrefix="user" value={tabValue} index={2}>
                <Grid container spacing={3}>
                  <AdminFormSection title="Notification Preferences" card>
                    {(formData.role === 'EVO_ADMIN' || formData.role === 'EVO_TEAM') ? (
                      <ToggleField
                        label="System Emails"
                        description="Receive system notification emails for sync failures, exceptions, and alerts."
                        checked={formData.system_emails}
                        onChange={(checked) => handleFieldChange('system_emails', checked)}
                        activeColor="info"
                      />
                    ) : (
                      <Alert severity="info">
                        System email notifications are only available for Evo Admin and Evo Team members.
                      </Alert>
                    )}
                  </AdminFormSection>
                </Grid>
              </FormTabPanel>
            )}

            {/* Tab 1 (FP_USER): FP Access */}
            {isFpUser && (
              <FormTabPanel idPrefix="user" value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <AdminFormSection
                    title="Franchise Partner Access"
                    card
                    caption="Manage which franchise partners this user can access."
                  >
                    <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'flex-start' }}>
                      <Autocomplete
                        sx={{ flex: 1 }}
                        options={filteredFpOptions}
                        getOptionLabel={(option) =>
                          `${option.fp_code} - ${option.franchise_name}`
                        }
                        value={selectedFp}
                        onChange={(_event, newValue) => setSelectedFp(newValue)}
                        inputValue={fpSearchText}
                        onInputChange={(_event, newValue) => setFpSearchText(newValue)}
                        loading={fpLoading}
                        noOptionsText={
                          fpSearchText.length < 2
                            ? 'Type at least 2 characters to search'
                            : 'No franchise partners found'
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search franchise partners"
                            placeholder="Type FP code or name..."
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.fp_code}>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {option.fp_code}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.franchise_name}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                      <Button
                        variant="contained"
                        onClick={handleAddFpAccess}
                        disabled={!selectedFp}
                        startIcon={<AddIcon />}
                        sx={{
                          bgcolor: poolwerxColors.teal,
                          '&:hover': { bgcolor: poolwerxColors.tealHover },
                          minWidth: 100,
                          height: 40
                        }}
                      >
                        Add
                      </Button>
                    </Box>

                    {fpAccessRows.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={ADMIN_TABLE_HEADER_SX}>FP Code</TableCell>
                              <TableCell sx={ADMIN_TABLE_HEADER_SX}>Franchise Name</TableCell>
                              <TableCell sx={ADMIN_TABLE_HEADER_SX}>Granted</TableCell>
                              <TableCell align="right" sx={ADMIN_TABLE_HEADER_SX}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {fpAccessRows.map((row) => (
                              <TableRow key={row.fp_code}>
                                <TableCell>
                                  <Chip
                                    label={row.fp_code}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell>{row.franchise_name}</TableCell>
                                <TableCell>
                                  {row.granted_at === 'Pending' ? (
                                    <Chip label="Pending" size="small" color="warning" />
                                  ) : (
                                    new Date(row.granted_at).toLocaleDateString()
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Remove access">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveFpAccess(row.fp_id, row.fp_code)}
                                      sx={inlineActionIconSx(poolwerxColors.stockNegative)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">
                        No franchise partner access assigned.
                        Use the search above to grant access.
                      </Alert>
                    )}
                  </AdminFormSection>
                </Grid>
              </FormTabPanel>
            )}
          </>
        )}
      </AdminFormCard>

      <AdminFormActions
        saveLabel={isEditMode ? 'Save Changes' : 'Create User'}
        saving={saving}
        saveDisabled={loading || isReadOnlyForViewer}
        onSave={handleSubmit}
        onCancel={handleCancel}
      />
    </Container>
  )
}

export default UserForm
